import { useState } from "react";
import Link from "next/link";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import LazyImage from "@/my_components/Common/LazyImage";
import { requestTicketLookupCode, verifyTicketLookupCode, downloadBookingPdf } from "@/services/eventService";
import { getErrorMessage } from "@/services/apiClient";
import { ROUTE } from "@/route/app_routes.js";
import { image_url } from "@/helper/Utilities";

const STEP_EMAIL = "email";
const STEP_CODE = "code";
const STEP_RESULTS = "results";

/**
 * Public, no-account-required "find my tickets" flow -- modeled on Eventbrite's
 * /signin/signup?referrer=/mytickets split-screen pattern (logo + big question + one input on a
 * white panel, a full-bleed photo on the other side). Three steps in one component rather than
 * three routes, since each step only makes sense right after the one before it (no reason to
 * deep-link into "enter your code" without having just requested one).
 */
const FindMyTickets = () => {
  const { t } = useTranslation("events");
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await requestTicketLookupCode(email.trim());
      toast.success(t("findTickets.codeSentToast", "If that email has any tickets, a code is on its way."));
      setStep(STEP_CODE);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await verifyTicketLookupCode(email.trim(), code.trim());
      setTickets(res.data || []);
      setStep(STEP_RESULTS);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    try {
      await requestTicketLookupCode(email.trim());
      toast.success(t("findTickets.codeResentToast", "A new code is on its way."));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDownload = async (booking) => {
    setDownloadingId(booking.booking_id);
    try {
      await downloadBookingPdf(booking.booking_id, `SmartTechAcademy-Ticket-${booking.booking_code}.pdf`);
    } catch (err) {
      toast.error(getErrorMessage(err) || t("findTickets.downloadError", "Could not download the ticket. Please try again."));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="rbt-find-tickets">
      <div className="rbt-find-tickets__panel">
        <Link href={ROUTE.events} className="rbt-find-tickets__logo">
          <LazyImage
            src={image_url("/images/logo/smarttech-full-blue.png")}
            alt="SmartTech Academy"
            width={190}
            height={72}
            wrapperStyle={{ backgroundColor: "transparent" }}
          />
        </Link>

        {step === STEP_EMAIL && (
          <>
            <h1 className="rbt-find-tickets__title">{t("findTickets.emailTitle", "What email did you use to book tickets?")}</h1>
            <p className="rbt-find-tickets__subtitle">
              {t("findTickets.emailSubtitle", "We'll send a 6-digit code to confirm it's you -- no account or password needed.")}
            </p>
            <form onSubmit={handleRequestCode} className="rbt-find-tickets__form">
              <label htmlFor="ft-email">{t("findTickets.emailLabel", "Email address")}</label>
              <input
                id="ft-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("findTickets.emailPlaceholder", "you@example.com")}
              />
              <button type="submit" className="rbt-btn btn-gradient w-100" disabled={busy}>
                {busy ? t("findTickets.sending", "Sending…") : t("findTickets.continue", "Continue")}
              </button>
            </form>
            <Link className="rbt-find-tickets__back" href={ROUTE.events}>
              {t("findTickets.back", "Back")}
            </Link>
          </>
        )}

        {step === STEP_CODE && (
          <>
            <h1 className="rbt-find-tickets__title">{t("findTickets.codeTitle", "Enter your code")}</h1>
            <p className="rbt-find-tickets__subtitle">
              {t("findTickets.codeSubtitle", "We sent a 6-digit code to {{email}}. It expires in 10 minutes.", { email })}
            </p>
            <form onSubmit={handleVerifyCode} className="rbt-find-tickets__form">
              <label htmlFor="ft-code">{t("findTickets.codeLabel", "Verification code")}</label>
              <input
                id="ft-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="rbt-find-tickets__code-input"
              />
              <button type="submit" className="rbt-btn btn-gradient w-100" disabled={busy}>
                {busy ? t("findTickets.checking", "Checking…") : t("findTickets.viewTickets", "View my tickets")}
              </button>
            </form>
            <button type="button" className="rbt-find-tickets__resend" onClick={handleResend}>
              {t("findTickets.resend", "Didn't get a code? Resend")}
            </button>
            <button type="button" className="rbt-find-tickets__back" onClick={() => setStep(STEP_EMAIL)}>
              {t("findTickets.back", "Back")}
            </button>
          </>
        )}

        {step === STEP_RESULTS && (
          <>
            <h1 className="rbt-find-tickets__title">
              {tickets.length > 0
                ? t("findTickets.resultsTitleFound", "Your tickets")
                : t("findTickets.resultsTitleEmpty", "No tickets found")}
            </h1>
            <p className="rbt-find-tickets__subtitle">
              {tickets.length > 0
                ? t("findTickets.resultsSubtitleFound", "Every active booking tied to {{email}}.", { email })
                : t("findTickets.resultsSubtitleEmpty", "We couldn't find any active bookings for {{email}}.", { email })}
            </p>

            {tickets.length > 0 ? (
              <div className="rbt-find-tickets__list">
                {tickets.map((ticket) => (
                  <div className="rbt-find-tickets__ticket" key={ticket.booking_id}>
                    <span className="rbt-find-tickets__ticket-media">
                      {ticket.event_image && <LazyImage src={ticket.event_image} alt={ticket.event_title} fill sizes="64px" />}
                    </span>
                    <span className="rbt-find-tickets__ticket-body">
                      <span className="rbt-find-tickets__ticket-title">{ticket.event_title}</span>
                      <span className="rbt-find-tickets__ticket-meta">{ticket.booking_code}</span>
                    </span>
                    <button
                      type="button"
                      className="rbt-btn btn-sm btn-border"
                      onClick={() => handleDownload(ticket)}
                      disabled={downloadingId === ticket.booking_id}
                    >
                      <i className="feather-download"></i>
                      {downloadingId === ticket.booking_id ? t("findTickets.preparing", "Preparing…") : t("findTickets.download", "Download")}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <Link className="rbt-btn btn-gradient w-100" href={ROUTE.events}>
                {t("findTickets.browseEvents", "Browse events")}
              </Link>
            )}

            <button type="button" className="rbt-find-tickets__back" onClick={() => { setStep(STEP_EMAIL); setEmail(""); setCode(""); }}>
              {t("findTickets.tryAnotherEmail", "Try a different email")}
            </button>
          </>
        )}
      </div>

      <div className="rbt-find-tickets__media">
        <LazyImage src={image_url("/app_images/event/find-tickets-side.webp")} alt="" fill sizes="50vw" priority />
        <div className="rbt-find-tickets__media-caption">
          {t("findTickets.mediaCaption", "Every ticket, one search away.")}
        </div>
      </div>
    </div>
  );
};

export default FindMyTickets;
